/* eslint-disable indent */
// src/components/TaskCard/index.tsx

"use client";

import React, { useState } from "react";
import { toast } from "react-toastify"; // Optional: For notifications
import { Award, CalendarCheck, Coins, Heart, LucideProps, MessageCircle, MessageSquare, ThumbsUp, Trophy, UserPlus } from "lucide-react";
import { claimTaskReward } from "@/lib/actions";
import { TaskDTO } from "@/types";
import { colourScheme } from "@/utils/styling/colours";
import * as Styled from "./TaskCard.styled";

// Mapping of icon names to lucide-react components
const iconMap: {
  [key: string]: React.ComponentType<LucideProps>;
} = {
  ThumbsUp: ThumbsUp,
  UserPlus: UserPlus,
  MessageSquare: MessageSquare,
  CalendarCheck: CalendarCheck,
  MessageCircle: MessageCircle,
  Award: Award,
  Heart: Heart,
  Trophy: Trophy
  // Add other icons as needed
};

// Function to get the Lucide icon component
const getIconComponent = (iconName: string) => {
  return iconMap[iconName] || Trophy;
};

// Function to determine icon size based on iconStyle
const getIconSize = (iconStyle: string): number => {
  switch (iconStyle) {
    case "icon-large":
      return 24;
    case "icon-medium":
      return 20;
    case "icon-small":
      return 16;
    default:
      return 20;
  }
};

// TaskCard component
const TaskCard: React.FC<{
  task: TaskDTO;
}> = ({
  task
}) => {
  const Icon = getIconComponent(task.iconName);
  const BadgeIcon = task.badgeIcon ? getIconComponent(task.badgeIcon) : Trophy;

  // Local state to handle claiming status
  const [isClaiming, setIsClaiming] = useState(false);
  const [isClaimed, setIsClaimed] = useState(task.completed);
  // Determine progress color based on task type
  const progressColor = task.taskType === "DAILY" ? colourScheme.bgAccent1 : colourScheme.bgAccent2;

  // Determine badge colors
  const badgeBorderColor = task.badgeColor || colourScheme.secondary;
  const badgeTextColor = task.badgeColor || colourScheme.secondary;

  // Determine status badge colors for daily tasks
  const statusBadgeColors = task.taskType === "DAILY" ? task.completed ? {
    bgColor: colourScheme.successDark,
    textColor: colourScheme.successLight
  } : {
    bgColor: colourScheme.secondaryDark,
    textColor: colourScheme.secondaryLight
  } : {};

  // Determine action button colors for regular tasks
  const actionButtonColors = task.taskType === "REGULAR" ? task.completed ? {
    bgColor: colourScheme.successDark,
    hoverBgColor: colourScheme.success,
    textColor: colourScheme.fullWhite
  } : {
    bgColor: colourScheme.alert,
    hoverBgColor: colourScheme.alertDark,
    textColor: colourScheme.white
  } : {};

  // Handler for claiming reward
  const handleClaimReward = async () => {
    if (task.completed || task.taskType !== "REGULAR") return;
    setIsClaiming(true);
    try {
      const result = await claimTaskReward(task.id);
      if (result.success) {
        task = {
          ...task,
          completed: true
        };
        setIsClaimed(true);
        toast.success("Reward claimed successfully!");
      }
      // Optionally, notify parent component or refetch tasks
    } catch (error) {
      console.error("Failed to claim reward:", error);
      toast.error("Failed to claim reward. Please try again.");
      // Optionally, handle error (e.g., show notification)
    } finally {
      setIsClaiming(false);
    }
  };
  return <Styled.TaskCardContainer completed={isClaimed} data-sentry-element="unknown" data-sentry-component="TaskCard" data-sentry-source-file="index.tsx">
            <Styled.TaskCardHeader data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.IconContainer completed={isClaimed} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Icon color={task.iconColor} size={getIconSize(task.iconStyle)} data-sentry-element="Icon" data-sentry-source-file="index.tsx" />
                </Styled.IconContainer>
                <div style={{
        flexGrow: 1
      }}>
                    <Styled.TaskTitle completed={isClaimed} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {task.title}
                    </Styled.TaskTitle>
                    <Styled.TaskDescription data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {task.description}
                    </Styled.TaskDescription>
                </div>
                <Styled.RewardContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Coins color={colourScheme.gold} size={14} data-sentry-element="Coins" data-sentry-source-file="index.tsx" />
                    <Styled.GoldAmount data-sentry-element="unknown" data-sentry-source-file="index.tsx">{task.reward}</Styled.GoldAmount>
                </Styled.RewardContainer>
            </Styled.TaskCardHeader>

            {!isClaimed && <Styled.ProgressBarContainer>
                    <Styled.ProgressBarBackground>
                        <Styled.ProgressBar progressColor={progressColor} initial={{
          width: 0
        }} animate={{
          width: `${task.currentProgress / task.goal * 100}%`
        }} transition={{
          duration: 1,
          ease: "easeOut"
        }} />
                    </Styled.ProgressBarBackground>
                    <Styled.ProgressText>
                        {task.currentProgress}/{task.goal}
                    </Styled.ProgressText>
                </Styled.ProgressBarContainer>}

            <Styled.TaskFooter data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                {task.taskType === "DAILY" ? <Styled.StyledBadge>Daily Task</Styled.StyledBadge> : <Styled.StyledBadge badgeColor={badgeTextColor} borderColor={badgeBorderColor}>
                        {task.badgeIcon && <Styled.BadgeIconStyled color={task.badgeColor}>
                                <BadgeIcon size={12} />
                            </Styled.BadgeIconStyled>}
                        {task.badgeName}
                    </Styled.StyledBadge>}

                {task.taskType === "DAILY" ? <Styled.StatusBadge bgColor={statusBadgeColors.bgColor || ""} textColor={statusBadgeColors.textColor || ""}>
                        {isClaimed ? "Completed" : "In Progress"}
                    </Styled.StatusBadge> : isClaimed ? <Styled.StatusBadge className="text-xs" bgColor={colourScheme.successDark} textColor={colourScheme.successLight}>
                        Reward Claimed
                    </Styled.StatusBadge> : task.currentProgress >= task.goal ? <Styled.ClaimButton onClick={handleClaimReward} disabled={isClaiming} aria-label={`Claim reward for ${task.title}`} bgColor={actionButtonColors.bgColor || ""} textColor={actionButtonColors.textColor || ""} hoverBgColor={actionButtonColors.hoverBgColor || ""}>
                        {isClaiming ? "Claiming..." : "Claim Reward"}
                    </Styled.ClaimButton> : <Styled.StatusBadge className="text-xs" bgColor={colourScheme.secondaryDark} textColor={colourScheme.secondaryLight}>
                        In Progress
                    </Styled.StatusBadge>}
            </Styled.TaskFooter>
        </Styled.TaskCardContainer>;
};
export default React.memo(TaskCard);